import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL, LOGO_URL } from "../../lib/leads";

const LINKS = [
  { label: "Fleet", href: "#fleet" },
  { label: "Routes", href: "#routes" },
  { label: "Tours", href: "#tours" },
  { label: "Areas", href: "#areas" },
  { label: "Why Us", href: "#manifesto" },
  { label: "Contact", href: "#contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-jodhpur-bg/95 backdrop-blur-md border-b border-jodhpur-primary/10 py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 flex items-center justify-between">
        <a href="#top" data-testid="logo" aria-label="Jodhpur Cabs — go to top" className="flex items-center gap-2.5 group">
          <img
            src={LOGO_URL}
            alt="Jodhpur Cabs logo"
            className="h-11 w-11 rounded-xl object-contain bg-white p-0.5 shadow-sm transition-transform group-hover:scale-105"
          />
          <span className={`font-display text-xl font-semibold tracking-tight transition-colors ${scrolled ? "text-jodhpur-primary" : "text-white"}`}>
            Jodhpur Cabs
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-8">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.label.toLowerCase().replace(" ", "-")}`}
              className={`text-sm font-medium transition-colors hover:text-jodhpur-accent ${scrolled ? "text-jodhpur-ink/80" : "text-white/85"}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={`tel:${PHONE_TEL}`}
            data-testid="nav-call-btn"
            className={`hidden sm:flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-all duration-300 ${
              scrolled
                ? "bg-jodhpur-primary text-white hover:bg-jodhpur-secondary"
                : "bg-white text-jodhpur-primary hover:bg-jodhpur-accent hover:text-white"
            }`}
          >
            <Phone className="h-4 w-4" />
            {PHONE_DISPLAY}
          </a>
          <button
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            className={`md:hidden p-2 rounded-lg ${scrolled ? "text-jodhpur-primary" : "text-white"}`}
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="md:hidden bg-jodhpur-bg border-t border-jodhpur-primary/10 mt-3"
          data-testid="mobile-menu"
        >
          <div className="px-6 py-4 flex flex-col gap-4">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="text-jodhpur-ink font-medium"
              >
                {l.label}
              </a>
            ))}
            <a href={`tel:${PHONE_TEL}`} className="flex items-center gap-2 text-jodhpur-primary font-semibold">
              <Phone className="h-4 w-4" /> {PHONE_DISPLAY}
            </a>
          </div>
        </motion.div>
      )}
    </motion.header>
  );
};
